import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
    ExternalIntegrationService,
    ExternalSyncResponse,
    IntegrationTestResponse,
    SyncExecution,
    SyncStatusSummary
} from '../../service/api/external-integration.service';
import {ExternalSyncDirectionEnum} from '../../enum/external-sync-direction.enum';

/**
 * External Integration Manager Component (F-030: Integration with External Marketing Tools).
 * Provides UI for managing external marketing tool integrations and syncing campaign members.
 *
 * Acceptance Criteria:
 * - Given campaign segment, when push to external tool is triggered, then member list is synced
 * - Given external tool integration, when member status changes externally, then status syncs back
 * - Given integration is configured, when campaign activates, then automatic sync can be enabled
 */
@Component({
    selector: 'ct-external-integration-manager',
    templateUrl: './external-integration-manager.component.html',
    standalone: false
})
export class ExternalIntegrationManagerComponent implements OnChanges {

    @Input() campaignId: number;
    @Input() campaignName: string;

    @Output() syncCompleted = new EventEmitter<ExternalSyncResponse>();
    @Output() integrationConfigured = new EventEmitter<any>();

    // Available integrations
    availableIntegrations: any[] = [];
    selectedIntegrationId: number;

    // Sync configurations for this campaign
    syncConfigurations: SyncStatusSummary[] = [];
    selectedSyncId: number;

    // Sync execution history
    syncHistory: SyncExecution[] = [];

    // UI state
    loading: boolean = false;
    syncing: boolean = false;
    testing: boolean = false;
    error: string = '';
    successMessage: string = '';
    activeTab: 'overview' | 'configure' | 'history' = 'overview';

    // Configuration form
    configSyncDirection: ExternalSyncDirectionEnum = ExternalSyncDirectionEnum.outbound;
    configAutoSync: boolean = false;

    syncDirectionOptions = [
        {value: ExternalSyncDirectionEnum.outbound, label: 'Ausgehend (Push nach extern)'},
        {value: ExternalSyncDirectionEnum.inbound, label: 'Eingehend (Pull von extern)'},
        {value: ExternalSyncDirectionEnum.bidirectional, label: 'Bidirektional (Zwei-Wege-Synchronisierung)'}
    ];

    constructor(private integrationService: ExternalIntegrationService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['campaignId'] && this.campaignId) {
            this.loadData();
        }
    }

    loadData(): void {
        if (!this.campaignId) return;

        this.loading = true;
        this.error = '';

        this.loadAvailableIntegrations();
        this.loadSyncConfigurations();
    }

    loadAvailableIntegrations(): void {
        this.integrationService.getActiveIntegrations().subscribe({
            next: (integrations) => {
                this.availableIntegrations = integrations;
                this.loading = false;
            },
            error: (err) => {
                console.error('Failed to load integrations', err);
                this.loading = false;
            }
        });
    }

    loadSyncConfigurations(): void {
        this.integrationService.getCampaignSyncStatus(this.campaignId).subscribe({
            next: (configs) => {
                this.syncConfigurations = configs;
                if (configs.length > 0 && !this.selectedSyncId) {
                    this.selectedSyncId = configs[0].campaignExternalSyncId;
                    this.loadSyncHistory(this.selectedSyncId);
                }
            },
            error: (err) => {
                console.error('Failed to load sync configurations', err);
            }
        });
    }

    loadSyncHistory(syncId: number): void {
        this.integrationService.getExecutionHistory(syncId, 10).subscribe({
            next: (history) => {
                this.syncHistory = history;
            },
            error: (err) => {
                console.error('Failed to load sync history', err);
            }
        });
    }

    testIntegration(integrationId: number): void {
        this.testing = true;
        this.error = '';
        this.successMessage = '';

        this.integrationService.testConnection(integrationId).subscribe({
            next: (response: IntegrationTestResponse) => {
                this.testing = false;
                if (response.success) {
                    this.successMessage = `Verbindung erfolgreich! Antwortzeit: ${response.responseTimeMs}ms`;
                } else {
                    this.error = response.message || 'Verbindungstest fehlgeschlagen';
                }
            },
            error: (err) => {
                this.testing = false;
                this.error = 'Integrationsverbindung konnte nicht getestet werden';
            }
        });
    }

    configureSync(): void {
        if (!this.campaignId || !this.selectedIntegrationId) {
            this.error = 'Bitte wählen Sie eine Integration aus';
            return;
        }

        this.loading = true;
        this.error = '';

        this.integrationService.configureCampaignSync(
            this.selectedIntegrationId,
            this.campaignId,
            this.configSyncDirection,
            this.configAutoSync
        ).subscribe({
            next: (result) => {
                this.loading = false;
                this.successMessage = 'Synchronisierungskonfiguration erfolgreich gespeichert';
                this.loadSyncConfigurations();
                this.integrationConfigured.emit(result);
            },
            error: (err) => {
                this.loading = false;
                this.error = 'Synchronisierung konnte nicht konfiguriert werden';
            }
        });
    }

    triggerSync(syncId: number, fullSync: boolean = false): void {
        this.syncing = true;
        this.error = '';
        this.successMessage = '';

        this.integrationService.triggerSync(syncId, fullSync).subscribe({
            next: (response: ExternalSyncResponse) => {
                this.syncing = false;
                if (response.success) {
                    this.successMessage = 'Synchronisierung erfolgreich gestartet';
                    this.loadSyncConfigurations();
                    this.loadSyncHistory(syncId);
                    this.syncCompleted.emit(response);
                } else {
                    this.error = response.message || 'Synchronisierung konnte nicht gestartet werden';
                }
            },
            error: (err) => {
                this.syncing = false;
                this.error = 'Synchronisierung konnte nicht ausgelöst werden';
            }
        });
    }

    retryFailedMembers(syncId: number): void {
        this.syncing = true;
        this.error = '';

        this.integrationService.retryFailedMembers(syncId).subscribe({
            next: (response: ExternalSyncResponse) => {
                this.syncing = false;
                if (response.success) {
                    this.successMessage = 'Erneuter Synchronisierungsversuch für fehlgeschlagene Mitglieder gestartet';
                    this.loadSyncConfigurations();
                    this.loadSyncHistory(syncId);
                } else {
                    this.error = response.message || 'Erneuter Versuch fehlgeschlagen';
                }
            },
            error: (err) => {
                this.syncing = false;
                this.error = 'Erneuter Synchronisierungsversuch fehlgeschlagen';
            }
        });
    }

    toggleAutoSync(syncId: number, enabled: boolean): void {
        this.integrationService.setAutoSync(syncId, enabled).subscribe({
            next: () => {
                this.successMessage = `Auto-Synchronisierung ${enabled ? 'aktiviert' : 'deaktiviert'}`;
                this.loadSyncConfigurations();
            },
            error: (err) => {
                this.error = 'Auto-Synchronisierung konnte nicht aktualisiert werden';
            }
        });
    }

    onSyncConfigChange(syncId: number): void {
        this.selectedSyncId = syncId;
        this.loadSyncHistory(syncId);
    }

    onTabChange(tab: 'overview' | 'configure' | 'history'): void {
        this.activeTab = tab;
        this.clearMessages();
    }

    getIntegrationTypeIcon(type: string): string {
        switch (type?.toUpperCase()) {
            case 'MAUTIC': return 'fa-solid fa-bullhorn';
            case 'RUDDERSTACK': return 'fa-solid fa-ship';
            case 'EMAIL_PLATFORM': return 'fa-solid fa-envelope';
            case 'SMS_PLATFORM': return 'fa-solid fa-message';
            case 'AD_PLATFORM': return 'fa-solid fa-ad';
            case 'CRM': return 'fa-solid fa-users';
            case 'CDP': return 'fa-solid fa-database';
            case 'CUSTOM': return 'fa-solid fa-plug';
            default: return 'fa-solid fa-plug';
        }
    }

    getIntegrationTypeColor(type: string): string {
        switch (type?.toUpperCase()) {
            case 'MAUTIC': return 'text-orange-600 bg-orange-100';
            case 'RUDDERSTACK': return 'text-blue-600 bg-blue-100';
            case 'EMAIL_PLATFORM': return 'text-green-600 bg-green-100';
            case 'SMS_PLATFORM': return 'text-yellow-600 bg-yellow-100';
            case 'AD_PLATFORM': return 'text-purple-600 bg-purple-100';
            case 'CRM': return 'text-indigo-600 bg-indigo-100';
            case 'CDP': return 'text-teal-600 bg-teal-100';
            case 'CUSTOM': return 'text-gray-600 bg-gray-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    }

    getSyncStatusClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'NOT_SYNCED': return 'text-gray-600 bg-gray-100';
            case 'PENDING': return 'text-gray-600 bg-gray-100';
            case 'SYNCING': return 'text-blue-600 bg-blue-100';
            case 'RUNNING': return 'text-blue-600 bg-blue-100';
            case 'COMPLETED': return 'text-green-600 bg-green-100';
            case 'FAILED': return 'text-red-600 bg-red-100';
            case 'PARTIAL': return 'text-yellow-600 bg-yellow-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    }

    getSyncStatusIcon(status: string): string {
        switch (status?.toUpperCase()) {
            case 'NOT_SYNCED': return 'fa-solid fa-minus';
            case 'PENDING': return 'fa-solid fa-clock';
            case 'SYNCING': return 'fa-solid fa-sync fa-spin';
            case 'RUNNING': return 'fa-solid fa-spinner fa-spin';
            case 'COMPLETED': return 'fa-solid fa-check';
            case 'FAILED': return 'fa-solid fa-times';
            case 'PARTIAL': return 'fa-solid fa-exclamation';
            default: return 'fa-solid fa-question';
        }
    }

    getSyncDirectionIcon(direction: string): string {
        switch (direction?.toUpperCase()) {
            case 'OUTBOUND': return 'fa-solid fa-arrow-right';
            case 'INBOUND': return 'fa-solid fa-arrow-left';
            case 'BIDIRECTIONAL': return 'fa-solid fa-arrows-left-right';
            default: return 'fa-solid fa-arrows-left-right';
        }
    }

    formatDate(date: Date): string {
        if (!date) return '-';
        return new Date(date).toLocaleString();
    }

    formatDuration(ms: number): string {
        if (!ms) return '-';
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    }

    clearMessages(): void {
        this.error = '';
        this.successMessage = '';
    }

    getSyncProgress(config: SyncStatusSummary): number {
        if (config.totalMembers === 0) return 0;
        return (config.syncedMembers / config.totalMembers) * 100;
    }

    hasFailedMembers(config: SyncStatusSummary): boolean {
        return config.failedMembers > 0;
    }
}
